/**
 * Incident stage model for the village/rural structure-fire workflow.
 * Stage drives which insights are relevant (see blueprint §6).
 */

export const INCIDENT_STAGES = [
  'Dispatch',
  'EnRoute',
  'Arrival',
  'Rescue',
  'Suppression',
  'Overhaul',
  'Investigation',
  'StandDown',
] as const;

export type IncidentStage = (typeof INCIDENT_STAGES)[number];

/** Human-facing label for a stage. */
export const STAGE_LABELS: Record<IncidentStage, string> = {
  Dispatch: 'Dispatch',
  EnRoute: 'En route',
  Arrival: 'Arrival',
  Rescue: 'Rescue',
  Suppression: 'Suppression',
  Overhaul: 'Overhaul',
  Investigation: 'Investigation / Handover',
  StandDown: 'Stand-down / Review',
};

export const stageIndex = (stage: IncidentStage): number =>
  INCIDENT_STAGES.indexOf(stage);

/** Linear next/previous helpers for the stage stepper (no wrap-around). */
export function nextStage(stage: IncidentStage): IncidentStage {
  const i = stageIndex(stage);
  return INCIDENT_STAGES[Math.min(i + 1, INCIDENT_STAGES.length - 1)]!;
}

export function previousStage(stage: IncidentStage): IncidentStage {
  const i = stageIndex(stage);
  return INCIDENT_STAGES[Math.max(i - 1, 0)]!;
}

/** True if `stage` is at or after `reference` in the linear flow. */
export const isAtOrAfter = (
  stage: IncidentStage,
  reference: IncidentStage,
): boolean => stageIndex(stage) >= stageIndex(reference);
